import { faWhatsapp } from "@fortawesome/free-brands-svg-icons/faWhatsapp";
import { faRobot } from "@fortawesome/free-solid-svg-icons/faRobot";
import { faTag } from "@fortawesome/free-solid-svg-icons/faTag";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../../../components/AtiraIcon";
import { Flex } from "../../../../../components/Flex";
import { Text } from "../../../../../components/Text";
import { ConversationMessage } from "../../../../../model/integration/dto/meta/MetaConversationMessage";
import { Rounded } from "../../../../../theme/Rounded";
import { Spacing } from "../../../../../theme/Spacing";

const Row = styled(Flex)`
  align-items: center;
  justify-content: center;
  gap: ${Spacing.s};
  margin: ${Spacing.s} 0;
`;

const Rule = styled.span`
  flex: 1;
  height: 1px;
  background-color: ${({ theme }) => theme.sub};
`;

const Note = styled(Flex)`
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.m};
  border-radius: ${Rounded.pill};
  background-color: ${({ theme }) => theme.lightSub};
  max-width: 70%;
`;

type Props = {
  message: ConversationMessage;
};

/** Something that happened to the conversation, rather than something someone
 * said: our own notes, which the customer never sees, and WhatsApp's.
 */
export const WASystemNote: React.FC<Props> = ({ message }) => {
  const { t } = useTranslation();

  const system = message.system;

  const agentName =
    system?.agentName || t("integration.whatsapp.meta.aiAgent.defaultName");

  const notice = system?.kind === "whatsapp_notice";
  const tagNote =
    system?.kind === "tag_added" || system?.kind === "tag_removed";

  const describe = () => {
    switch (system?.kind) {
      case "whatsapp_notice":
        return t("integration.whatsapp.meta.whatsappNotice");
      case "takeover":
        return t("integration.whatsapp.meta.handover.takeover", {
          name: agentName,
        });
      case "handover":
        return system.reason
          ? t("integration.whatsapp.meta.handover.note", {
              name: agentName,
              reason: system.reason,
            })
          : t("integration.whatsapp.meta.handover.noteNoReason", {
              name: agentName,
            });
      case "tag_added":
      case "tag_removed":
        return t(`integration.whatsapp.meta.tagNote.${system.kind}`, {
          name: system.byName || t("integration.whatsapp.meta.tagNote.someone"),
          tag: system.tagName,
        });
      // Notes from before `system` existed, and kinds newer than this app
      default:
        return message.text?.body;
    }
  };

  const body = describe();

  return (
    <Row>
      <Rule />

      <Note>
        <AtiraIcon
          icon={notice ? faWhatsapp : tagNote ? faTag : faRobot}
          size="xs"
          color="darkerSub"
        />
        <Text
          fontSize="xs"
          color="darkerSub"
          align="center"
          style={{ unicodeBidi: "plaintext" }}
        >
          {body}
        </Text>
      </Note>

      <Rule />
    </Row>
  );
};
